import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { decidirOAuth, fetchSolicitudOAuth } from '../api';
import { ErrorBox, Loading } from '../components/ui';

/** Qué significa cada permiso, en palabras de quien lo concede. */
const PERMISOS: Record<string, string> = {
  'tasks:read':
    'Ver las tareas que tienes asignadas: descripción, estado, comentarios y la lista de adjuntos.',
  'tasks:write':
    'Cambiar el estado de tus tareas y comentarlas. Claude te pedirá confirmación antes de cada cambio.',
  offline_access:
    'Mantener la conexión hasta 30 días sin volver a preguntarte. Puedes cortarla cuando quieras.',
};

/**
 * Pantalla de consentimiento OAuth: una aplicación (Claude) pide acceso al MCP en nombre de
 * quien tiene la sesión. Se llega desde /api/oauth/authorize, que ya ha comprobado que la
 * aplicación es de Anthropic y que la vuelta es suya.
 *
 * Lo que se enseña es lo que se ha COMPROBADO —el host de la aplicación y a dónde vuelve—,
 * no el nombre que la aplicación dice tener. Y se muestran los permisos que se van a
 * conceder de verdad: a un observador nunca se le ofrece escribir.
 */
export function AutorizarMcpPage(): JSX.Element {
  const [params] = useSearchParams();
  const id = params.get('solicitud') ?? '';
  const solicitud = useQuery({
    queryKey: ['oauth-solicitud', id],
    queryFn: () => fetchSolicitudOAuth(id),
    enabled: id !== '',
    retry: false,
  });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const decidir = async (aprobar: boolean): Promise<void> => {
    setEnviando(true);
    setError(null);
    try {
      const { redireccion } = await decidirOAuth(id, aprobar);
      // Vuelta a Claude, con el código o con la negativa.
      window.location.assign(redireccion);
    } catch (caught) {
      setError(caught);
      setEnviando(false);
    }
  };

  return (
    <div className="mx-auto mt-16 max-w-md px-6 text-[13px]">
      <h1 className="text-lg font-semibold">Conectar con el Software Command Center</h1>

      {!id && <p className="mt-4">Falta la solicitud. Vuelve a conectar desde Claude.</p>}
      {solicitud.isPending && id && <Loading />}
      {solicitud.isError && (
        <div className="mt-4">
          <ErrorBox error={solicitud.error} />
          <p className="mt-3 text-[var(--color-ink-muted)]">
            Si tardaste más de diez minutos, la solicitud caducó: vuelve a conectar desde Claude.
          </p>
        </div>
      )}

      {solicitud.data && (
        <>
          <p className="mt-4">
            <strong>{solicitud.data.cliente}</strong>
            {solicitud.data.nombre && (
              <span className="text-[var(--color-ink-muted)]"> («{solicitud.data.nombre}»)</span>
            )}{' '}
            pide acceso a tu cuenta <strong>@{solicitud.data.login}</strong> para:
          </p>
          <ul className="mt-3 list-disc space-y-1.5 pl-5">
            {solicitud.data.scopes.map((s) => (
              <li key={s}>{PERMISOS[s] ?? s}</li>
            ))}
          </ul>
          {solicitud.data.rol === 'observer' && (
            <p className="mt-3 text-[var(--color-ink-muted)]">
              Tu cuenta es de observador: la conexión solo podrá consultar, no cambiar nada.
            </p>
          )}
          <p className="mt-4 text-[var(--color-ink-muted)]">
            Solo tus tareas asignadas, nada más del portal. Después volverás a{' '}
            <strong>{solicitud.data.destino}</strong>.
          </p>
          {solicitud.data.loopback && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
              La respuesta va a un programa de este ordenador (Claude Code). Permite solo si acabas
              de pedirlo tú desde Claude Code.
            </div>
          )}

          {error !== null && (
            <div className="mt-4">
              <ErrorBox error={error} />
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              disabled={enviando}
              onClick={() => void decidir(true)}
              className="rounded-lg bg-[var(--color-ink)] px-4 py-2 font-medium text-[var(--color-surface)] disabled:opacity-50"
            >
              Permitir
            </button>
            <button
              type="button"
              disabled={enviando}
              onClick={() => void decidir(false)}
              className="rounded-lg border border-[var(--color-border-subtle)] px-4 py-2 disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
          <p className="mt-6 text-[12px] text-[var(--color-ink-muted)]">
            Las conexiones activas se ven y se cortan en <Link to="/conexiones">Conexiones</Link>.
          </p>
        </>
      )}
    </div>
  );
}
